const DEFAULT_CURRENCY_CONFIG = {
  ARS: {
    locale: 'es-AR',
    symbol: '$',
  },
  BRL: {
    locale: 'pt-BR',
    symbol: 'R$',
  },
  USD: {
    locale: 'en-US',
    symbol: '$',
  },
};

export function formatCurrency(value, options = {}) {
  const {
    currency = 'ARS',
    locale,
    symbol,
    compact = false,
    stripZeroDecimals = false,
  } = options;

  const currencyConfig = DEFAULT_CURRENCY_CONFIG[currency] || DEFAULT_CURRENCY_CONFIG.ARS;
  const resolvedLocale = locale || currencyConfig.locale;
  const resolvedSymbol = symbol || currencyConfig.symbol;

  const amount = Number(value);
  if (!Number.isFinite(amount)) return compact ? `${resolvedSymbol} 0` : `${resolvedSymbol}0,00`;

  const absoluteAmount = Math.abs(amount);
  const hasIntegerValue = Math.round(absoluteAmount * 100) % 100 === 0;
  const fractionDigits = stripZeroDecimals && hasIntegerValue ? 0 : 2;
  const signPrefix = amount < 0 ? '-' : '';

  try {
    const formatted = new Intl.NumberFormat(resolvedLocale, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(absoluteAmount);
    return `${signPrefix}${resolvedSymbol} ${formatted}`;
  } catch {
    const fallback = fractionDigits === 0
      ? String(Math.round(absoluteAmount))
      : absoluteAmount.toFixed(2);
    return `${signPrefix}${resolvedSymbol} ${fallback}`;
  }
}

/**
 * Formats an amount for display without currency symbol: thousands separator ".",
 * decimal separator "," and no decimals when the amount is a whole number.
 */
export function formatAmount(value, options = {}) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '';
  const rounded = Math.round(Math.abs(amount) * 100) / 100;
  const locale = options.locale || DEFAULT_CURRENCY_CONFIG.ARS.locale;
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: Number.isInteger(rounded) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(rounded);
  return `${amount < 0 ? '-' : ''}${formatted}`;
}

/**
 * Parses what a user types into a price field. Accepts the es-AR notation
 * ("1.234,56") and plain numbers ("1234.56"). Returns null when it is not a
 * positive amount.
 */
export function parseLocalizedAmount(rawValue) {
  const text = String(rawValue ?? '').replace(/[$\s]/g, '');
  if (!text) return null;

  let normalized = text;
  if (text.includes(',')) {
    normalized = text.replace(/\./g, '').replace(',', '.');
  } else if (/^\d{1,3}(\.\d{3})+$/.test(text)) {
    normalized = text.replace(/\./g, '');
  }

  if (!/^\d+(\.\d+)?$/.test(normalized)) return null;
  const amount = Number(normalized);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  return Number(amount.toFixed(2));
}
